import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, IconArrowLeft } from '@flixbus/honeycomb-icons-react';
import { IconAttention } from '@flixbus/honeycomb-icons-react';
import { Button, Popup, PopupSection, Heading } from '@flixbus/honeycomb-react';

import { useVoucherFormContext } from '../Context';
import { generateMultipleVouchers, generateVoucher, saveComment } from '../../../apis';
import styles from './voucher-form-submit.module.scss';
import { useNotification } from '../../Notifications/Context';
import VoucherFormSummary from './VoucherFormSummary';
import { FormMeta, readFormMeta } from './readFormMeta';
import { findInvalidField } from './findInvalidField';

const HIGH_VALUE_THRESHOLD = 300;
const HIGH_VALUE_CURRENCIES = new Set(['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD']);
const MONETARY_DISCOUNT_TYPES = new Set([
  'credit_amount',
  'value_marketing',
  'value_hybrid',
  'free_ride_hybrid',
]);

const getVoucherAmount = (discountType: {
  type?: string;
  credit?: number;
  discount?: number;
}): number => {
  if (discountType.type === 'value_marketing') return discountType.discount ?? 0;
  return discountType.credit ?? 0;
};

type SubmitStep = 'idle' | 'summary' | 'highValue';

const VoucherFormSubmit = () => {
  const { pushToast } = useNotification();
  const {
    formState: { payload, activeDiscount, comment },
    formErrors,
  } = useVoucherFormContext();
  const navigate = useNavigate();
  const { count } = payload;
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<SubmitStep>('idle');
  const [formMeta, setFormMeta] = useState<FormMeta>({});
  const confirmButtonRef = useRef<HTMLDivElement>(null);

  const isHighValue = () => {
    const amount = getVoucherAmount(payload.discountType);
    const currency = payload.discountType.currency || 'EUR';

    return (
      amount > HIGH_VALUE_THRESHOLD &&
      MONETARY_DISCOUNT_TYPES.has(payload.discountType.type || '') &&
      HIGH_VALUE_CURRENCIES.has(currency)
    );
  };

  const handleSubmit = () => {
    if (submitting) return;

    if (Object.keys(formErrors).length > 0) {
      pushToast({
        message: Object.values(formErrors)[0],
        status: 'warning',
      });
      return;
    }

    const form = confirmButtonRef.current?.closest('form');
    const invalidField = findInvalidField(form);

    if (invalidField) {
      invalidField.element.focus();
      invalidField.element.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      pushToast({
        message: invalidField.message,
        status: 'warning',
      });
      return;
    }

    if (
      ['credit_amount', 'value_marketing', 'value_hybrid'].includes(activeDiscount) &&
      payload.discountType.currency &&
      payload.discountType.currency === 'MXN' &&
      !payload.entity
    ) {
      pushToast({
        message: 'Please select a contract!',
        status: 'warning',
      });
      return;
    }

    setFormMeta(readFormMeta(form));
    setStep('summary');
  };

  const handleSummaryConfirm = () => {
    if (isHighValue()) {
      setStep('highValue');
      return;
    }

    generate();
  };

  const generate = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const res =
        count === 1 ? await generateVoucher(payload) : await generateMultipleVouchers(payload);

      if (comment && comment.length > 0) {
        try {
          count === 1 && (await saveComment(res.id, { comment: comment }));
        } catch (e) {
          const message = e instanceof Error ? e.message : 'An unknown error occurred';

          pushToast({
            message,
            status: 'danger',
          });
        }
      }

      count === 1
        ? navigate(`/success/${res.id}`, { state: { voucher: res } })
        : navigate(`/batch/${res.id}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      let detail = message;
      try {
        detail = JSON.parse(message)?.detail || message;
      } catch {
        // Backend returned a non-JSON (plain text) error body.
      }

      pushToast({
        message: detail,
        status: 'danger',
      });
      setStep('idle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Button Elem='a' appearance='link' RouterLink={Link} to='/'>
        <Icon InlineIcon={IconArrowLeft} />
        Cancel
      </Button>

      <div ref={confirmButtonRef} style={{ display: 'inline-block' }}>
        <Button
          size='md'
          appearance='primary'
          type='submit'
          loading={submitting}
          disabled={submitting}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          Confirm voucher creation
        </Button>
      </div>

      <Popup
        active={step === 'summary'}
        onOverlayClick={() => setStep('idle')}
        targetRef={confirmButtonRef}
      >
        <PopupSection type='title'>
          <Heading size={2} sectionHeader flushSpace>
            Review voucher generation
          </Heading>
        </PopupSection>
        <PopupSection type='content'>
          <div className={styles.summary}>
            <VoucherFormSummary meta={formMeta} />
          </div>
        </PopupSection>
        <PopupSection type='actions'>
          <Button onClick={() => setStep('idle')}>Go back</Button>
          <Button
            appearance='primary'
            loading={submitting}
            disabled={submitting}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              handleSummaryConfirm();
            }}
          >
            Confirm & generate
          </Button>
        </PopupSection>
      </Popup>

      <Popup
        active={step === 'highValue'}
        onOverlayClick={() => setStep('idle')}
        targetRef={confirmButtonRef}
      >
        <PopupSection type='icon'>
          <Icon InlineIcon={IconAttention} size={12} style={{ fill: 'var(--hcr-warning-color)' }} />
        </PopupSection>
        <PopupSection type='title'>
          <Heading size={2} sectionHeader flushSpace>
            High value voucher
          </Heading>
        </PopupSection>
        <PopupSection type='content'>
          You are about to create a voucher worth{' '}
          <b>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: payload.discountType.currency || 'EUR',
              minimumFractionDigits: 2,
            }).format(getVoucherAmount(payload.discountType))}
          </b>
          . Please double-check the amount is correct before proceeding.
        </PopupSection>
        <PopupSection type='actions'>
          <Button onClick={() => setStep('idle')}>Go Back</Button>
          <Button
            appearance='primary'
            loading={submitting}
            disabled={submitting}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              generate();
            }}
          >
            Confirm
          </Button>
        </PopupSection>
      </Popup>
    </div>
  );
};

export default VoucherFormSubmit;
